import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { usePermissions } from '../context/PermissionsContext';
import { getUsers } from '../api';
import api from '../api';
import { IconEdit, IconDelete } from '../components/Icons';

const ROLES = [
  { value: 'gpf_administrator',   label: 'Administrator',   desc: 'Full access — manages projects, users, settings and reports' },
  { value: 'gpf_project_manager', label: 'Project Manager', desc: 'Creates and manages projects & tasks, views reports. Cannot manage users' },
  { value: 'gpf_project_user',    label: 'Team Member',     desc: 'Works on assigned tasks, adds notes & files. Cannot add/edit projects' },
  { value: 'gpf_client',          label: 'Client / Guest',  desc: 'Read-only — can only view projects and tasks assigned to them' },
];

const emptyForm = {
  username: '', email: '', password: '',
  first_name: '', last_name: '', roles: 'gpf_project_user',
};

export default function UsersPage() {
  const { can } = usePermissions();
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form,     setForm]     = useState(emptyForm);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [search,     setSearch]     = useState('');
  const [editUser,   setEditUser]   = useState(null);
  const [editForm,   setEditForm]   = useState({ roles: 'gpf_project_user', first_name: '', last_name: '', email: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError,  setEditError]  = useState('');

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/gpf/v1/create-user', {
        username:   form.username,
        email:      form.email,
        password:   form.password,
        first_name: form.first_name,
        last_name:  form.last_name,
        role:       form.roles,
      });
      setForm(emptyForm);
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      const rawMsg = err.response?.data?.message || 'Failed to create user.';
      // Strip HTML tags from WordPress error messages
      const cleanMsg = rawMsg.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      setError(cleanMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (id, name) => {
    const currentUser = JSON.parse(localStorage.getItem('gpf_user') || '{}');
    if (String(id) === String(currentUser.id)) {
      alert("You can't delete your own account. Ask another administrator, or use Settings \u2192 Delete My Account if you want to close the whole workspace.");
      return;
    }
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/wp/v2/users/${id}?force=true&reassign=1`);
      setUsers(users.filter(u => u.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user.');
    }
  };

  const openEdit = async (user) => {
    setEditUser(user);
    setEditError('');
    // Fetch full user data including meta fields
    try {
      const res = await api.get(`/wp/v2/users/${user.id}?context=edit`);
      const u = res.data;
      setEditForm({
        roles:         u.roles?.[0]                       || 'gpf_project_user',
        first_name:    u.first_name                       || '',
        last_name:     u.last_name                        || '',
        email:         u.email                            || '',
        job_title:     u.gpf_job_title                    || u.meta?.gpf_job_title || '',
        phone:         u.gpf_phone                        || u.meta?.gpf_phone     || '',
        address:       u.gpf_address                      || u.meta?.gpf_address   || '',
        timezone:      u.gpf_timezone                     || u.meta?.gpf_timezone  || '',
        profile_image: u.gpf_profile_image                || u.meta?.gpf_profile_image || '',
      });
    } catch {
      setEditForm({
        roles: user.roles?.[0] || 'gpf_project_user',
        first_name: user.first_name || '',
        last_name:  user.last_name  || '',
        email:      user.email      || '',
        job_title: '', phone: '', address: '', timezone: '', profile_image: '',
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    setEditSaving(true);
    setEditError('');
    try {
      // Use custom endpoint to bypass WP role restrictions
      await api.post('/gpf/v1/update-user', {
        user_id:    editUser.id,
        first_name: editForm.first_name,
        last_name:  editForm.last_name,
        email:      editForm.email,
        role:       editForm.roles,
      });
      // Update profile meta fields
      await api.post('/gpf/v1/profile', {
        user_id:       editUser.id,
        gpf_job_title: editForm.job_title,
        gpf_phone:     editForm.phone,
        gpf_address:   editForm.address,
        gpf_timezone:  editForm.timezone,
      });
      setEditUser(null);
      fetchUsers();
    } catch(err) {
      setEditError(err.response?.data?.message || 'Failed to update user.');
    } finally { setEditSaving(false); }
  };

  const handleEditAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !editUser) return;
    const formData = new FormData();
    formData.append('image', file);
    formData.append('user_id', editUser.id);
    try {
      const res = await api.post('/gpf/v1/profile/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditForm(prev => ({ ...prev, profile_image: res.data.url }));
    } catch(err) { setEditError('Image upload failed.'); }
  };

  const getRoleLabel = (user) => {
    const role = user.roles?.[0] || '';
    return ROLES.find(r => r.value === role)?.label || role || 'No Role';
  };

  const getRoleStyle = (user) => {
    const role = user.roles?.[0] || '';
    switch (role) {
      case 'gpf_system_admin':    return { bg: '#fdf2f8', text: '#9333ea' };
      case 'gpf_administrator':   return { bg: '#eff6ff', text: '#2563eb' };
      case 'gpf_project_manager': return { bg: '#f0fdf4', text: '#16a34a' };
      case 'gpf_project_user':    return { bg: '#fffbeb', text: '#d97706' };
      case 'gpf_client':          return { bg: '#f8fafc', text: '#64748b' };
      default:                    return { bg: '#f8fafc', text: '#64748b' };
    }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.slug?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout>
      <div style={styles.page}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Users</h1>
            <p style={styles.subtitle}>{users.length} total user{users.length !== 1 ? 's' : ''}</p>
          </div>
          <button style={styles.primaryBtn} onClick={() => setShowForm(true)}>
            + Add User
          </button>
        </div>

        {/* Search */}
        <div style={styles.searchRow}>
          <input
            style={styles.searchInput}
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Create user form */}
        {showForm && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>New User</h2>
            <form onSubmit={handleCreateUser} style={styles.form}>
              <div style={styles.formGrid}>
                <div style={styles.field}>
                  <label style={styles.label}>First Name *</label>
                  <input style={styles.input} value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Last Name</label>
                  <input style={styles.input} value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Username *</label>
                  <input style={styles.input} value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Email *</label>
                  <input style={styles.input} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Password *</label>
                  <input style={styles.input} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Role *</label>
                  <select style={styles.input} value={form.roles} onChange={e => setForm({ ...form, roles: e.target.value })}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    {form.roles && (() => {
                      const found = ROLES.find(r => r.value === form.roles);
                      return found ? <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0', fontStyle: 'italic' }}>{found.desc}</p> : null;
                    })()}
                  </select>
                </div>
              </div>
              {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 14px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
          <p style={{ margin: 0, fontSize: 13, color: '#dc2626', lineHeight: 1.6, flex: 1 }}>{error}</p>
          {/upgrade|plan allows/i.test(error) && (
            <a href="/workspace?upgrade=1" style={{ fontSize: 13, fontWeight: 700, color: '#fb923c', textDecoration: 'none', whiteSpace: 'nowrap' }}>Upgrade Plan →</a>
          )}
        </div>
      )}
              <div style={styles.formActions}>
                <button type="button" style={styles.cancelBtn} onClick={() => { setShowForm(false); setForm(emptyForm); setError(''); }}>Cancel</button>
                <button type="submit" style={styles.primaryBtn} disabled={saving}>{saving ? 'Creating...' : 'Create User'}</button>
              </div>
            </form>
          </div>
        )}

        {/* Users list */}
        {loading ? (
          <p style={styles.loading}>Loading users...</p>
        ) : filtered.length === 0 ? (
          <div style={styles.empty}>
            <p style={styles.emptyText}>{search ? 'No users match your search.' : 'No users found.'}</p>
          </div>
        ) : (
          <div style={styles.userList}>
            <div style={styles.tableHeader}>
              <span style={{ flex: 2 }}>Name</span>
              <span style={{ flex: 2 }}>Email</span>
              <span style={{ flex: 1 }}>Username</span>
              <span style={{ flex: 1, textAlign: 'center' }}>Role</span>
              <span style={{ flex: 1, textAlign: 'right'  }}>Actions</span>
            </div>

            {filtered.map(user => {
              const roleStyle = getRoleStyle(user);
              const initials  = (user.name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

              return (
                <div key={user.id} style={styles.userRow}>
                  <div style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={styles.avatar}>{initials}</div>
                    <span style={styles.userName}>{user.name || user.slug}</span>
                  </div>
                  <div style={{ flex: 2, fontSize: 13, color: '#64748b' }}>
                    {user.email || '—'}
                  </div>
                  <div style={{ flex: 1, fontSize: 13, color: '#94a3b8' }}>
                    {user.slug}
                  </div>
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <span style={{ ...styles.badge, background: roleStyle.bg, color: roleStyle.text }}>
                      {getRoleLabel(user)}
                    </span>
                  </div>
                  <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button
                      style={styles.userActionBtn}
                      onClick={() => openEdit(user)}
                      title="Edit user"
                    >
                      <IconEdit width={14} height={14} />
                      Edit
                    </button>
                    <button
                      style={{ ...styles.userActionBtn, color: '#dc2626', borderColor: '#fecaca' }}
                      onClick={() => handleDeleteUser(user.id, user.name)}
                      title="Delete user"
                    >
                      <IconDelete width={14} height={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Role legend */}
        <div style={styles.legend}>
          {ROLES.map(r => {
            const s = getRoleStyle({ roles: [r.value] });
            return (
              <div key={r.value} style={styles.legendItem}>
                <span style={{ ...styles.legendDot, background: s.text }} />
                <span style={styles.legendLabel}>{r.label}</span>
              </div>
            );
          })}
        </div>

      </div>
      {/* Edit User Modal */}
      {editUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            {/* Modal header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>Edit User</h2>
              <button onClick={() => setEditUser(null)} style={{ background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {editError && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12, padding: '8px 12px', background: '#fef2f2', borderRadius: 8 }}>{editError}</p>}

              {/* Avatar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {editForm.profile_image
                    ? <img src={editForm.profile_image} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontSize: 22, fontWeight: 700, color: '#2563eb' }}>{(editForm.first_name?.[0] || '?').toUpperCase()}</span>
                  }
                </div>
                <div>
                  <label style={{ ...styles.label, cursor: 'pointer', color: '#2563eb' }}>
                    📷 Upload Photo
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleEditAvatarUpload} />
                  </label>
                  <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0' }}>JPG, PNG up to 2MB</p>
                </div>
              </div>

              {/* Name row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={styles.label}>First Name</label>
                  <input style={styles.input} value={editForm.first_name} onChange={e => setEditForm({...editForm, first_name: e.target.value})} placeholder="First name" />
                </div>
                <div>
                  <label style={styles.label}>Last Name</label>
                  <input style={styles.input} value={editForm.last_name} onChange={e => setEditForm({...editForm, last_name: e.target.value})} placeholder="Last name" />
                </div>
              </div>

              {/* Email */}
              <div style={{ marginBottom: 14 }}>
                <label style={styles.label}>Email Address</label>
                <input style={styles.input} type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} placeholder="email@company.com" />
              </div>

              {/* Job Title */}
              <div style={{ marginBottom: 14 }}>
                <label style={styles.label}>Job Title</label>
                <input style={styles.input} value={editForm.job_title} onChange={e => setEditForm({...editForm, job_title: e.target.value})} placeholder="e.g. Project Manager" />
              </div>

              {/* Phone */}
              <div style={{ marginBottom: 14 }}>
                <label style={styles.label}>Phone</label>
                <input style={styles.input} value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} placeholder="+1 (555) 000-0000" />
              </div>

              {/* Address */}
              <div style={{ marginBottom: 14 }}>
                <label style={styles.label}>Address</label>
                <input style={styles.input} value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} placeholder="City, Country" />
              </div>

              {/* Timezone */}
              <div style={{ marginBottom: 14 }}>
                <label style={styles.label}>Timezone</label>
                <select style={styles.input} value={editForm.timezone} onChange={e => setEditForm({...editForm, timezone: e.target.value})}>
                  <option value="">Select timezone...</option>
                  {['America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Toronto','America/Sao_Paulo','Europe/London','Europe/Paris','Europe/Berlin','Africa/Lagos','Africa/Nairobi','Africa/Johannesburg','Asia/Dubai','Asia/Kolkata','Asia/Singapore','Asia/Tokyo','Australia/Sydney','Pacific/Auckland'].map(tz => (
                    <option key={tz} value={tz}>{tz.replace(/_/g,' ').replace('/',' / ')}</option>
                  ))}
                </select>
              </div>

              {/* Role */}
              <div style={{ marginBottom: 8 }}>
                <label style={styles.label}>Role</label>
                <select style={styles.input} value={editForm.roles} onChange={e => setEditForm({...editForm, roles: e.target.value})}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                {editForm.roles && (() => {
                  const found = ROLES.find(r => r.value === editForm.roles);
                  return found ? <p style={{ fontSize: 12, color: '#64748b', margin: '6px 0 0', fontStyle: 'italic', padding: '6px 10px', background: '#f8fafc', borderRadius: 6 }}>{found.desc}</p> : null;
                })()}
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={{ padding: '10px 20px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 }} onClick={() => setEditUser(null)}>Cancel</button>
              <button style={{ padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: editSaving ? 0.7 : 1 }} onClick={handleSaveEdit} disabled={editSaving}>
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const styles = {
  page:        { maxWidth: 900 },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title:       { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle:    { color: '#64748b', fontSize: 14, margin: '4px 0 0' },
  primaryBtn:  { padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  searchRow:   { marginBottom: 20 },
  searchInput: { padding: '9px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', width: 280 },
  formCard:    { background: '#fff', borderRadius: 10, padding: 24, marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  formTitle:   { fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 16px' },
  form:        { display: 'flex', flexDirection: 'column', gap: 16 },
  formGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  field:       { display: 'flex', flexDirection: 'column', gap: 6 },
  label:       { fontSize: 13, fontWeight: 600, color: '#374151' },
  input:       { padding: '9px 12px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  error:       { color: '#ef4444', fontSize: 13, margin: 0 },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: 12 },
  cancelBtn:   { padding: '9px 18px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  loading:     { color: '#64748b', fontSize: 14 },
  empty:       { background: '#fff', borderRadius: 10, padding: 48, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  emptyText:   { color: '#94a3b8', fontSize: 14 },
  userList:    { background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  tableHeader: { display: 'flex', alignItems: 'center', padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' },
  userRow:     { display: 'flex', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #f1f5f9', gap: 8 },
  avatar:      { width: 34, height: 34, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 },
  userName:    { fontSize: 14, fontWeight: 600, color: '#0f172a' },
  badge:       { padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, display: 'inline-block' },
  iconBtn:     { width: 28, height: 28, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' },
  userActionBtn: { display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, background: '#fff', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#374151', transition: 'background 0.15s' },
  legend:      { display: 'flex', gap: 20, marginTop: 24, padding: '12px 16px', background: '#fff', borderRadius: 8, width: 'fit-content', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', flexWrap: 'wrap' },
  legendItem:  { display: 'flex', alignItems: 'center', gap: 6 },
  legendDot:   { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  legendLabel: { fontSize: 12, color: '#64748b', fontWeight: 500 },
};